import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip } from "@/components/Chart";
import { Text } from "@/components/podkit/typography/Text";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import type { FC, ReactElement } from "react";
import * as RechartsPrimitive from "recharts";

type BaseInsightLineChartProps = {
    isLoading: boolean;
    chartData: unknown[];
    chartConfig: Record<string, { label?: string; color?: string }>;
    tooltipContent: ReactElement;
    yAxisConfig?: {
        width?: number;
        tickFormatter?: (value: number) => string;
        domain?: [number | string, number | string];
        minTickGap?: number;
        allowDecimals?: boolean;
    };
    children: ReactElement | ReactElement[]; // Line components
};

export const BaseInsightLineChart: FC<BaseInsightLineChartProps> = ({
    isLoading,
    chartData,
    chartConfig,
    tooltipContent,
    yAxisConfig = {},
    children,
}) => {

    if (isLoading) {
        return (
            <div className="flex h-[300px] items-center justify-center gap-2 md:h-[400px]" role="status" aria-live="polite">
                <IconSpinner className="animate-spin" size="base" aria-hidden={true} />
                <Text>Loading chart data...</Text>
            </div>
        );
    }

    if (chartData.length === 0) {
        return (
            <div className="flex h-[300px] items-center justify-center md:h-[400px]" role="status" aria-live="polite">
                <Text>No data available for the selected time range</Text>
            </div>
        );
    }

    const {
        width = 40,
        tickFormatter,
        domain = [0, "auto"],
        minTickGap = 20,
        allowDecimals = false,
    } = yAxisConfig;

    return (
        <ChartContainer className="-mr-4 h-[300px] w-full md:h-[400px]" config={chartConfig}>
            <RechartsPrimitive.LineChart data={chartData} margin={{ top: 10, left: 0 }}>
                <RechartsPrimitive.CartesianGrid vertical={false} strokeDasharray="3 3" />
                <RechartsPrimitive.XAxis
                    xAxisId="0"
                    dataKey="timestamp"
                    tickFormatter={(timestamp: number) => {
                        const date = new Date(timestamp);
                        return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                    }}
                    type="number"
                    domain={["dataMin", "dataMax"]}
                    padding={{ left: 5, right: 5 }}
                    scale="time"
                    minTickGap={20}
                    tickCount={15}
                    tickMargin={8}
                />
                <RechartsPrimitive.YAxis
                    width={width}
                    tickFormatter={tickFormatter}
                    domain={domain}
                    minTickGap={minTickGap}
                    allowDecimals={allowDecimals}
                />
                <ChartTooltip
                    cursor={{ stroke: "rgb(var(--border-base))", strokeWidth: 1 }}
                    content={tooltipContent}
                />
                {children}
                <ChartLegend className="justify-end" content={<ChartLegendContent />} />
            </RechartsPrimitive.LineChart>
        </ChartContainer>
    );
};
